import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import {
  Plus,
  Settings,
  Play,
  Eye,
  Users,
  BarChart3,
  FileText,
  Image as ImageIcon,
  Video,
  List,
  CheckSquare,
  PenTool,
  MessageSquare,
  Cloud,
  Gamepad2,
  Target,
  FileImage,
  Globe,
  Orbit,
  Music,
  CheckCircle,
  ChevronRight,
  Search,
  FolderOpen,
  Trash2,
  Copy,
  X,
  GripVertical,
  ArrowLeft,
  Save,
  BarChart2,
  Type,
  Link2,
  Trophy,
  Pencil,
  StickyNote,
  Brain,
  Images,
  Tag,
  AlertCircle,
  Edit3,
  Calculator,
  Clock as ClockIcon,
  Loader2,
  Download,
  Columns,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  loadLessonWithSlides,
  addSlideToLesson,
  updateSlideContent,
  deleteSlideAndReorder,
  duplicateSlide,
  moveSlide,
  publishLesson,
  unpublishLesson,
  updateLessonMetadata,
  deleteLessonWithValidation,
  type LessonWithSlides
} from '../../utils/supabase/lesson-manager';
import { getCurrentUser } from '../../utils/supabase/client';
import type { Slide } from '../../utils/supabase/client';
import { DeleteLessonModal } from '../components/DeleteLessonModal';
import { UploadableFileInput } from '../components/common/UploadableFileInput';
import { UploadableImageInput } from '../components/common/UploadableImageInput';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import RichTextEditor from '../components/common/RichTextEditor';
import { getAllSlideTypes } from '../../utils/slides/slideRegistry';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { generateLessonPlanDocx } from '../../utils/export/docx-generator';
import { generateLessonPptx } from '../../utils/export/pptx-generator';
import DraggableSlideList from '../components/slides/DraggableSlideList';
import DraggableSlideStrip from '../components/slides/DraggableSlideStrip';
import { StudentSlideView, TeacherSlideView } from '../components/slides';
import { arrayMove } from '@dnd-kit/sortable';

type SlideType = Slide['type'];

interface SlideTypeOption {
  value: SlideType;
  label: string;
  icon: any;
  category: 'Content' | 'Interactive';
}

// Helper function to strip HTML tags for preview
function stripHtml(html: string): string {
  if (!html) return '';
  // Use regex to strip HTML tags
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

export default function LessonBuilder() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [lesson, setLesson] = useState<LessonWithSlides | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [lessonCoverImage, setLessonCoverImage] = useState<string | null>(null);
  const [showLessonDetails, setShowLessonDetails] = useState(false);
  const [slideTitle, setSlideTitle] = useState('');
  const [slideContent, setSlideContent] = useState<any>({});
  const [previewMode, setPreviewMode] = useState<'student' | 'teacher' | 'editor'>('student');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSlideTypeForAdd, setSelectedSlideTypeForAdd] = useState<SlideType | ''>('');

  const slideTypes: SlideTypeOption[] = getAllSlideTypes().map((config) => ({
    value: config.type,
    label: config.label,
    icon: config.icon,
    category: config.category === 'content' ? 'Content' : 'Interactive',
  }));

  // Load lesson data on mount
  useEffect(() => {
    if (lessonId) {
      loadLesson();
    }
  }, [lessonId]);

  // Update local state when selected slide changes
  useEffect(() => {
    if (slides[selectedSlide]) {
      const currentSlide = slides[selectedSlide];
      // For text_image slides, fallback to heading if title doesn't exist
      const title = currentSlide.type === 'text_image' 
        ? (currentSlide.content?.title || currentSlide.content?.heading || '')
        : (currentSlide.content?.title || '');
      setSlideTitle(title);
      setSlideContent(currentSlide.content || {});
    }
  }, [selectedSlide, slides]);

  const loadLesson = async () => {
    if (!lessonId) return;

    setLoading(true);
    try {
      // Verify user is authenticated
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in to continue');
        navigate('/teacher/login');
        return;
      }

      // Load lesson with slides
      const lessonData = await loadLessonWithSlides(lessonId);

      // Verify user owns this lesson
      if (lessonData.teacher_id !== user.id) {
        toast.error('You do not have permission to edit this lesson');
        navigate('/teacher/dashboard');
        return;
      }

      setLesson(lessonData);
      setSlides(lessonData.slides);
      setLessonTitle(lessonData.title);
      setLessonDescription(lessonData.description || '');
      setLessonCoverImage(lessonData.cover_image || null);

      // Select first slide if available
      if (lessonData.slides.length > 0) {
        setSelectedSlide(0);
      }
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Failed to load lesson');
      navigate('/teacher/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlide = async (type: SlideType) => {
    if (!lessonId) return;

    try {
      const typeLabel = slideTypes.find(t => t.value === type)?.label || 'Slide';
      const newSlide = await addSlideToLesson(lessonId, type, `New ${typeLabel}`);
      
      setSlides([...slides, newSlide]);
      setSelectedSlide(slides.length);
      // Keep the selected slide type to allow quick addition of multiple slides of the same type
      setSelectedSlideTypeForAdd(type);
      toast.success('Slide added');
    } catch (error) {
      console.error('Error adding slide:', error);
      toast.error('Failed to add slide');
    }
  };

  const handleDeleteSlide = async (index: number) => {
    if (!lessonId || slides.length === 0) return;

    const slideToDelete = slides[index];
    
    try {
      await deleteSlideAndReorder(lessonId, slideToDelete.id);
      
      const newSlides = slides.filter((_, i) => i !== index);
      setSlides(newSlides);
      
      // Adjust selected slide
      if (selectedSlide >= newSlides.length) {
        setSelectedSlide(Math.max(0, newSlides.length - 1));
      }
      
      toast.success('Slide deleted');
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error('Failed to delete slide');
    }
  };

  const handleDuplicateSlide = async (index: number) => {
    const slideToDuplicate = slides[index];
    
    try {
      const newSlide = await duplicateSlide(slideToDuplicate.id);
      setSlides([...slides, newSlide]);
      toast.success('Slide duplicated');
    } catch (error) {
      console.error('Error duplicating slide:', error);
      toast.error('Failed to duplicate slide');
    }
  };

  const handleSaveSlideContent = async () => {
    if (!slides[selectedSlide]) return;

    setSaving(true);
    try {
      const currentSlide = slides[selectedSlide];
      const updatedContent = {
        ...slideContent,
        title: slideTitle
      };

      // For text_image slides, sync title with heading
      if (currentSlide.type === 'text_image') {
        updatedContent.heading = slideTitle;
      }

      await updateSlideContent(currentSlide.id, updatedContent);

      // Update local state
      const updatedSlides = [...slides];
      updatedSlides[selectedSlide] = {
        ...currentSlide,
        content: updatedContent
      };
      setSlides(updatedSlides);

      toast.success('Slide saved');
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error('Failed to save slide');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLessonMetadata = async () => {
    if (!lessonId) return;

    setSaving(true);
    try {
      const updatedLesson = await updateLessonMetadata(lessonId, {
        title: lessonTitle,
        description: lessonDescription,
        cover_image: lessonCoverImage
      });

      setLesson(current => current ? { ...current, ...updatedLesson } : current);
      toast.success('Lesson updated');
      setShowLessonDetails(false);
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error('Failed to update lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleLessonDetailsOpenChange = (open: boolean) => {
    if (lesson) {
      setLessonTitle(lesson.title);
      setLessonDescription(lesson.description || '');
      setLessonCoverImage(lesson.cover_image || null);
    }
    setShowLessonDetails(open);
  };

  const handlePublish = async () => {
    if (!lessonId || !lesson) return;

    if (slides.length === 0) {
      toast.error('Add at least one slide before publishing');
      return;
    }

    try {
      if (lesson.status === 'published') {
        await unpublishLesson(lessonId);
        setLesson({ ...lesson, status: 'draft' });
        toast.success('Lesson unpublished');
      } else {
        await publishLesson(lessonId);
        setLesson({ ...lesson, status: 'published' });
        toast.success('Lesson published!');
      }
    } catch (error) {
      console.error('Error publishing lesson:', error);
      toast.error('Failed to publish lesson');
    }
  };

  const handleStartLive = () => {
    if (!lesson) return;
    
    if (lesson.status !== 'published') {
      toast.error('Please publish the lesson before starting a session');
      return;
    }

    if (slides.length === 0) {
      toast.error('Add slides before starting a session');
      return;
    }

    // Navigate to dashboard to start session
    navigate(`/teacher/dashboard?tab=lessons`);
    toast.success('Return to dashboard to start a session');
  };

  const getSlideIcon = (type: string) => {
    const slideType = slideTypes.find(t => t.value === type);
    return slideType?.icon || FileText;
  };

  const handleReorderSlide = async (oldIndex: number, newIndex: number) => {
    if (!lessonId || oldIndex === newIndex) return;

    const slideToMove = slides[oldIndex];

    // Optimistic local reorder
    const reorderedSlides = arrayMove(slides, oldIndex, newIndex);
    setSlides(reorderedSlides);

    // Adjust selected slide to follow the moved slide or stay correct
    if (selectedSlide === oldIndex) {
      setSelectedSlide(newIndex);
    } else if (oldIndex < selectedSlide && newIndex >= selectedSlide) {
      setSelectedSlide(selectedSlide - 1);
    } else if (oldIndex > selectedSlide && newIndex <= selectedSlide) {
      setSelectedSlide(selectedSlide + 1);
    }

    try {
      await moveSlide(lessonId, slideToMove.id, newIndex);
    } catch (error) {
      console.error('Error reordering slide:', error);
      toast.error('Failed to reorder slide');
      // Revert on error
      loadLesson();
    }
  };

  const updateSlideField = (field: string, value: any) => {
    setSlideContent((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteLesson = async (lessonId: string) => {
    setIsDeleting(true);
    try {
      const result = await deleteLessonWithValidation(lessonId);
      
      if (result.success) {
        toast.success('Lesson deleted successfully');
        // Navigate back to dashboard
        navigate('/teacher/dashboard?tab=lessons');
      } else {
        // Show specific error message
        toast.error(result.error || 'Failed to delete lesson');
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error('Delete lesson error:', error);
      toast.error('An unexpected error occurred');
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Lesson not found</h2>
          <Button onClick={() => navigate('/teacher/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-background overflow-hidden">
      {/* Left Sidebar - Slide Navigator */}
      <aside className="hidden lg:flex w-full lg:w-80 bg-card border-r border-border flex-col">
        <div className="p-4 border-b border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/teacher/dashboard')}
            className="mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h2 className="font-semibold text-lg">Slide Navigator</h2>
          <p className="text-sm text-muted-foreground">{slides.length} slides</p>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          {slides.length === 0 ? (
            <div className="text-center py-8 px-4">
              <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
              <p className="text-sm text-muted-foreground">No slides yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add your first slide below</p>
            </div>
          ) : (
            <DraggableSlideList
              slides={slides}
              selectedSlide={selectedSlide}
              slideTypes={slideTypes}
              getSlideIcon={getSlideIcon}
              onSelectSlide={setSelectedSlide}
              onDuplicateSlide={handleDuplicateSlide}
              onDeleteSlide={(index) => {
                handleDeleteSlide(index);
              }}
              onReorder={handleReorderSlide}
            />
          )}
        </ScrollArea>

        <div className="p-4 border-t border-border">
          <Select value={selectedSlideTypeForAdd} onValueChange={(value) => handleAddSlide(value as SlideType)}>
            <SelectTrigger className="w-full rounded-xl">
              <SelectValue placeholder="Add New Slide" />
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2 px-2">CONTENT SLIDES</p>
                {slideTypes.filter(t => t.category === 'Content').map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </div>
              <div className="p-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground mb-2 px-2">INTERACTIVE ACTIVITIES</p>
                {slideTypes.filter(t => t.category === 'Interactive').map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>
      </aside>

      {/* Center - Slide Preview Canvas */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Toolbar */}
        <header className="bg-card border-b border-border px-3 md:px-6 py-3 space-y-3 sm:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-0 sm:justify-between min-w-0">
            {/* Left section - Title and Back Button */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1 sm:flex-initial">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/teacher/dashboard')}
                className="lg:hidden flex-shrink-0 h-10 w-10"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-0 flex-1 flex items-center gap-2 md:gap-3">
                <Input
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  onBlur={handleSaveLessonMetadata}
                  className="font-semibold text-base md:text-lg border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 min-w-0 truncate"
                  placeholder="Lesson Title"
                  title={lessonTitle}
                />
                <Badge variant={lesson.status === 'published' ? 'default' : 'outline'} className="flex-shrink-0 hidden sm:inline-flex">
                  {lesson.status === 'published' ? 'Published' : 'Draft'}
                </Badge>
              </div>
            </div>
            {/* Right section - Action Buttons */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg flex-1 sm:flex-none min-h-10"
                onClick={() => handleLessonDetailsOpenChange(true)}
              >
                <Settings className="w-4 h-4" />
                <span className="ml-2">Details</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg flex-1 sm:flex-none min-h-10"
                    disabled={slides.length === 0}
                  >
                    <Download className="w-4 h-4" />
                    <span className="ml-2 hidden sm:inline">Export</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={async () => {
                    if (!lesson) return;
                    toast.info('Generating Word document...');
                    try {
                      await generateLessonPlanDocx({ lesson, slides, includeAnswers: true });
                      toast.success('Lesson plan exported');
                    } catch (e) { toast.error('Export failed'); }
                  }}>
                    <FileText className="w-4 h-4 mr-2" />
                    Lesson Plan (.docx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => {
                    if (!lesson) return;
                    toast.info('Generating PowerPoint...');
                    try {
                      await generateLessonPptx({ lesson, slides, includeAnswers: true });
                      toast.success('Presentation exported');
                    } catch (e) { toast.error('Export failed'); }
                  }}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Presentation (.pptx)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg flex-1 sm:flex-none min-h-10"
                onClick={handleSaveSlideContent}
                disabled={saving || slides.length === 0}
                aria-label={saving ? 'Saving slide' : 'Save slide'}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span className="ml-2 hidden sm:inline">Save</span>
              </Button>
              <Button
                size="sm"
                className="rounded-lg bg-accent hover:bg-accent/90 flex-1 sm:flex-none min-h-10"
                onClick={handleStartLive}
                disabled={lesson.status !== 'published' || slides.length === 0}
                aria-label={lesson.status === 'published' ? 'Start live session' : 'Publish lesson to start'}
              >
                <Play className="w-4 h-4" />
                <span className="ml-2 hidden sm:inline">Start</span>
              </Button>
              <Button
                size="sm"
                className="rounded-lg flex-1 sm:flex-none min-h-10"
                onClick={handlePublish}
                disabled={slides.length === 0}
                aria-label={lesson.status === 'published' ? 'Unpublish lesson' : 'Publish lesson'}
              >
                <span className="hidden sm:inline">{lesson.status === 'published' ? 'Unpublish' : 'Publish'}</span>
                <span className="sm:hidden">{lesson.status === 'published' ? 'Unpub' : 'Pub'}</span>
              </Button>
            </div>
            {/* Mobile Status Badge - Show below on small screens */}
            <Badge variant={lesson.status === 'published' ? 'default' : 'outline'} className="sm:hidden self-start">
              {lesson.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
          </div>
        </header>

        {/* Mobile Slide Navigator - Horizontal Scroll with Drag & Drop */}
        <div className="lg:hidden border-b border-border bg-card overflow-hidden">
          <ScrollArea className="w-full">
            <div className="flex gap-2 p-3">
              <DraggableSlideStrip
                slides={slides}
                selectedSlide={selectedSlide}
                slideTypes={slideTypes}
                getSlideIcon={getSlideIcon}
                onSelectSlide={setSelectedSlide}
                onDuplicateSlide={handleDuplicateSlide}
                onDeleteSlide={(index) => handleDeleteSlide(index)}
                onReorder={handleReorderSlide}
              />
              <Select value={selectedSlideTypeForAdd} onValueChange={(value) => handleAddSlide(value as SlideType)}>
                <SelectTrigger className="flex-shrink-0 h-12 w-12 rounded-lg border-2 border-dashed min-h-12 min-w-12" aria-label="Add new slide">
                  <Plus className="w-4 h-4" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 px-2">CONTENT SLIDES</p>
                    {slideTypes.filter(t => t.category === 'Content').map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                  <div className="p-2 border-t">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 px-2">INTERACTIVE</p>
                    {slideTypes.filter(t => t.category === 'Interactive').map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                </SelectContent>
              </Select>
            </div>
          </ScrollArea>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto p-4 md:p-8 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            {slides.length === 0 ? (
              <div className="bg-card rounded-2xl border-2 border-dashed border-border shadow-xl aspect-video flex items-center justify-center p-12">
                <div className="text-center">
                  <Plus className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">No slides yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first slide using the dropdown on the left
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-semibold">Slide Preview</h3>
                    <p className="text-xs text-muted-foreground">Preview updates as you adjust this slide.</p>
                  </div>
                  <div className="inline-flex rounded-lg border border-border bg-card p-1" role="tablist" aria-label="Slide preview mode">
                    <Button
                      type="button"
                      size="sm"
                      variant={previewMode === 'student' ? 'default' : 'ghost'}
                      role="tab"
                      aria-selected={previewMode === 'student'}
                      onClick={() => setPreviewMode('student')}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Student View
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={previewMode === 'teacher' ? 'default' : 'ghost'}
                      role="tab"
                      aria-selected={previewMode === 'teacher'}
                      onClick={() => setPreviewMode('teacher')}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Teacher View
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={previewMode === 'editor' ? 'default' : 'ghost'}
                      role="tab"
                      aria-selected={previewMode === 'editor'}
                      onClick={() => setPreviewMode('editor')}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Editor
                    </Button>
                  </div>
                </div>
                <div
                  className={`bg-card rounded-2xl border-2 border-border shadow-xl ${
                    previewMode === 'editor'
                      ? 'aspect-video flex items-center justify-center p-12'
                      : 'max-h-[calc(100vh-16rem)] overflow-auto p-4 md:p-8'
                  }`}
                >
                  <SlidePreview
                    key={`${slides[selectedSlide].id}-${previewMode}`}
                    slide={slides[selectedSlide]}
                    content={{
                      ...slideContent,
                      title: slideTitle,
                      ...(slides[selectedSlide].type === 'text_image' ? { heading: slideTitle } : {}),
                    }}
                    viewMode={previewMode}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Right Sidebar - Slide Settings */}
      <aside className="hidden lg:block w-80 bg-card border-l border-border overflow-auto">
        {slides.length === 0 ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 p-6 text-center">
              <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-sm text-muted-foreground">
                Add a slide to see settings
              </p>
            </div>
            
            {/* Danger Zone - Delete Lesson */}
            <div className="p-6 border-t border-border bg-destructive/5">
              <h4 className="text-sm font-semibold text-destructive mb-2">Danger Zone</h4>
              <p className="text-xs text-muted-foreground mb-3">
                Permanently delete this lesson and all its data
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Lesson
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <div className="flex-1 p-6 overflow-auto">
              <h3 className="font-semibold text-lg mb-1">Slide Settings</h3>
              <p className="text-sm text-muted-foreground mb-6">
                {slideTypes.find(t => t.value === slides[selectedSlide]?.type)?.label}
              </p>

              <div className="space-y-6">
                <div>
                  <Label>Slide Title</Label>
                  <Input
                    value={slideTitle}
                    onChange={(e) => setSlideTitle(e.target.value)}
                    className="mt-2 rounded-xl"
                    placeholder="Enter slide title..."
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    <Label>Show Title on Slide</Label>
                  </div>
                  <Switch
                    checked={slideContent?.showTitle !== false}
                    onCheckedChange={(checked) => updateSlideField('showTitle', checked)}
                  />
                </div>

                <SlideSettings
                  slide={slides[selectedSlide]}
                  content={slideContent}
                  onUpdate={updateSlideField}
                />

                <div className="pt-4 border-t">
                  <Button
                    onClick={handleSaveSlideContent}
                    className="w-full rounded-xl"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Slide
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Danger Zone - Delete Lesson */}
            <div className="p-6 border-t border-border bg-destructive/5">
              <h4 className="text-sm font-semibold text-destructive mb-2">Danger Zone</h4>
              <p className="text-xs text-muted-foreground mb-3">
                Permanently delete this lesson and all its data
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Lesson
              </Button>
            </div>
          </div>
        )}
      </aside>

      <Dialog open={showLessonDetails} onOpenChange={handleLessonDetailsOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lesson Details</DialogTitle>
            <DialogDescription>Update the lesson title, description, and cover image.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lesson-title">Title</Label>
              <Input
                id="lesson-title"
                value={lessonTitle}
                onChange={(event) => setLessonTitle(event.target.value)}
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lesson-description">Description</Label>
              <Textarea
                id="lesson-description"
                value={lessonDescription}
                onChange={(event) => setLessonDescription(event.target.value)}
                maxLength={300}
                rows={4}
              />
            </div>
            <UploadableImageInput
              currentImageUrl={lessonCoverImage}
              onImageUpload={setLessonCoverImage}
              onImageRemove={() => setLessonCoverImage(null)}
              deleteOnRemove={false}
              label="Cover image"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleLessonDetailsOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveLessonMetadata} disabled={saving || !lessonTitle.trim()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lesson Modal */}
      {lessonId && (
        <DeleteLessonModal
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          lessonTitle={lessonTitle}
          lessonId={lessonId}
          onConfirmDelete={handleDeleteLesson}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}

function InteractiveSlidePreview({ slide }: { slide: Slide }) {
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  return (
    <div>
      <div className="mb-6 flex justify-center">
        <Badge variant="outline">Student preview · responses are not saved</Badge>
      </div>
      <StudentSlideView
        slide={slide}
        onSubmit={() => setHasSubmitted(true)}
        onAnswerChange={setSelectedAnswer}
        selectedAnswer={selectedAnswer}
        hasSubmitted={hasSubmitted}
      />
    </div>
  );
}

// Slide Preview Component
function SlidePreview({
  slide,
  content,
  viewMode = 'editor',
}: {
  slide: Slide;
  content: any;
  viewMode?: 'student' | 'teacher' | 'editor';
}) {
  if (!slide) return null;
  const previewSlide = { ...slide, content };

  if (viewMode === 'student') {
    return <InteractiveSlidePreview slide={previewSlide} />;
  }

  if (viewMode === 'teacher') {
    return <TeacherSlideView slide={previewSlide} />;
  }

  const renderPreviewContent = () => {
    switch (slide.type) {
      case 'text_image':
        return content?.layout === 'two-column' ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              {content?.imageUrl && (
                <img src={content.imageUrl} alt="Preview 1" className="w-full rounded-lg" onError={(e) => (e.currentTarget.style.display = 'none')} />
              )}
              <p className="text-sm text-muted-foreground">{stripHtml(content?.content) || 'Column 1'}</p>
            </div>
            <div className="space-y-2">
              {content?.imageUrl2 && (
                <img src={content.imageUrl2} alt="Preview 2" className="w-full rounded-lg" onError={(e) => (e.currentTarget.style.display = 'none')} />
              )}
              <p className="text-sm text-muted-foreground">{stripHtml(content?.content2) || 'Column 2'}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{stripHtml(content?.content) || 'No content added yet'}</p>
            {content?.imageUrl && (
              <img src={content.imageUrl} alt="Preview" className="max-w-xs rounded-lg" onError={(e) => (e.currentTarget.style.display = 'none')} />
            )}
          </div>
        );
      case 'multiple_choice':
        return (
          <div className="space-y-3 text-left">
            <p className="font-semibold">{content?.question || 'Question?'}</p>
            <div className="space-y-2">
              {(content?.options || []).map((opt: string, idx: number) => (
                <div key={idx} className="text-sm p-2 border rounded">
                  {opt}
                </div>
              ))}
            </div>
          </div>
        );
      case 'true_false':
        return (
          <div className="space-y-4">
            <p className="font-semibold">{content?.question || 'True or False?'}</p>
            <div className="flex gap-4">
              <button className="px-6 py-2 border rounded">True</button>
              <button className="px-6 py-2 border rounded">False</button>
            </div>
          </div>
        );
      case 'open_ended':
        return (
          <div className="space-y-3">
            <p className="font-semibold">{content?.question || 'Your question here'}</p>
            <textarea className="w-full p-2 border rounded text-sm" placeholder={content?.placeholder || 'Type your answer here...'} disabled />
          </div>
        );
      case 'poll':
        return (
          <div className="space-y-3 text-left">
            <p className="font-semibold">{content?.question || 'Poll question?'}</p>
            <div className="space-y-2">
              {(content?.options || []).map((opt: string, idx: number) => (
                <button key={idx} className="w-full text-left text-sm p-2 border rounded hover:bg-muted">
                  ○ {opt}
                </button>
              ))}
            </div>
          </div>
        );
      case 'video':
        return (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{content?.provider || 'youtube'} video</p>
            <div className="bg-black rounded aspect-video flex items-center justify-center">
              <Video className="w-12 h-12 text-muted-foreground" />
            </div>
          </div>
        );
      case 'word_cloud':
        return (
          <div className="space-y-3">
            <p className="font-semibold">{content?.question || 'What word comes to mind?'}</p>
            <p className="text-sm text-muted-foreground">Max {content?.maxWords || 3} words per student</p>
          </div>
        );
      case 'web_content':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Web Content</p>
            <p className="text-xs text-muted-foreground truncate">{content?.url || 'No URL set'}</p>
            {content?.description && <p className="text-sm">{content.description}</p>}
          </div>
        );
      case 'field_trip':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">{content?.location || 'Virtual Field Trip'}</p>
            <p className="text-xs text-muted-foreground">{content?.description || 'Panorama experience'}</p>
            {content?.panoramaMode === 'upload' && content?.panoramaImageUrl && (
              <img
                src={content.panoramaImageUrl}
                alt="360° panorama preview"
                className="w-full h-32 object-cover rounded-lg border border-border"
              />
            )}
            {(!content?.panoramaMode || content?.panoramaMode === 'url') && content?.panoramaUrl && (
              <p className="text-xs text-primary truncate">{content.panoramaUrl}</p>
            )}
          </div>
        );
      case 'audio':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">{content?.audioTitle || 'Audio Content'}</p>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Music className="w-4 h-4" />
              <div className="flex-1 h-1 bg-primary rounded"></div>
            </div>
          </div>
        );
      case 'pdf':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">PDF Document</p>
            <p className="text-xs text-muted-foreground truncate">{content?.pdfUrl || 'No PDF URL set'}</p>
          </div>
        );
      case 'text_only':
        return (
          <div className="space-y-2">
            <div
              className="rounded-lg p-4 min-h-[60px] flex items-center justify-center border border-border"
              style={{ backgroundColor: content?.backgroundColor || '#ffffff' }}
            >
              <p className="text-sm text-muted-foreground">
                {stripHtml(content?.content) || 'Text only slide'}
              </p>
            </div>
          </div>
        );
      case 'fill_blanks':
        return (
          <div className="space-y-2">
            <p className="text-sm">{content?.sentence || 'Sentence with blanks'}</p>
            <p className="text-xs text-muted-foreground">{(content?.answers || []).length} answers</p>
          </div>
        );
      case 'matching_pairs':
        return (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{content?.instruction || 'Matching activity'}</p>
            <div className="flex justify-between gap-4">
              <div className="flex-1 space-y-1">
                {(content?.pairs || []).slice(0, 3).map((pair: any, idx: number) => (
                  <div key={idx} className="text-xs p-1 border rounded">{pair.left}</div>
                ))}
              </div>
              <div className="flex-1 space-y-1">
                {(content?.pairs || []).slice(0, 3).map((pair: any, idx: number) => (
                  <div key={idx} className="text-xs p-1 border rounded">{pair.right}</div>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{(content?.pairs || []).length} pairs</p>
          </div>
        );
      case 'drag_drop':
        return (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{content?.instruction || 'Drag & Drop activity'}</p>
            <div className="flex flex-wrap gap-1">
              {(content?.items || []).slice(0, 4).map((item: any, idx: number) => (
                <div key={idx} className="text-xs px-2 py-1 bg-muted border rounded-lg">{typeof item === 'string' ? item : item.label}</div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{(content?.targets || []).length} categories</p>
          </div>
        );
      case 'sequencing':
        return (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{content?.instruction || 'Sequencing activity'}</p>
            <div className="space-y-1">
              {(content?.items || []).slice(0, 4).map((item: any, idx: number) => (
                <div key={idx} className="text-xs px-2 py-1 bg-muted border rounded-lg flex items-center gap-1">
                  <span className="font-semibold">{item.label}</span>
                  {item.description && <span className="text-muted-foreground">— {item.description}</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{(content?.items || []).length} items to order</p>
          </div>
        );
      case 'time_to_climb':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">{content?.questions?.[0]?.question || content?.question || 'Timed question'}</p>
            <p className="text-xs text-muted-foreground">
              {(content?.questions?.length || 1)} question{(content?.questions?.length || 1) > 1 ? 's' : ''} &middot; {content?.timeLimit || 30}s {content?.timeLimitMode === 'all_questions' ? 'total' : 'per question'}
            </p>
          </div>
        );
      case 'draw_it':
        return (
          <div className="space-y-2">
            <div className="bg-gray-100 rounded aspect-square flex items-center justify-center">
              <Pencil className="w-12 h-12 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">{content?.instructions || 'Draw your answer'}</p>
          </div>
        );
      case 'collaborate_board':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">{content?.template || 'blank'} board</p>
            <p className="text-sm">{content?.prompt || 'Collaborate with classmates'}</p>
            <p className="text-xs text-muted-foreground">
              Max notes per student: {content?.maxNotes ?? 3}
            </p>
          </div>
        );
      case 'label_diagram':
        return (
          <div className="space-y-2">
            {content?.imageUrl && (
              <img src={content.imageUrl} alt="Diagram" className="max-w-xs rounded-lg" onError={(e) => (e.currentTarget.style.display = 'none')} />
            )}
            <p className="text-sm text-muted-foreground">{content?.instructions || 'Label the diagram'}</p>
          </div>
        );
      case 'quiz':
        return (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Quiz Activity</p>
            <p className="text-xs text-muted-foreground">
              {(content?.questions?.length || 0)} question{(content?.questions?.length || 0) !== 1 ? 's' : ''}
              {content?.timeLimit > 0 && ` • ${content.timeLimit}s time limit`}
            </p>
            {content?.questions?.[0] && (
              <div className="text-xs p-2 bg-muted/50 rounded">
                <p className="font-medium">{content.questions[0].question}</p>
              </div>
            )}
          </div>
        );
      default:
        return (
          <p className="text-muted-foreground">
            Edit slide content in the settings panel →
          </p>
        );
    }
  };

  return (
    <div className="text-center">
      {content?.showTitle !== false && (
        <h2 className="text-2xl font-bold mb-4">{content?.title || 'Untitled Slide'}</h2>
      )}
      <Badge variant="outline" className="mb-6">{slide.type.replace('_', ' ').toUpperCase()}</Badge>
      <div className="bg-muted rounded-lg p-6">
        {renderPreviewContent()}
      </div>
    </div>
  );
}

function ActivityTimeLimitSetting({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (seconds: number) => void;
}) {
  const parsedValue = Number(value);
  const timeLimit = Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 60;

  return (
    <div>
      <Label>Time Limit (seconds)</Label>
      <Input
        type="number"
        value={timeLimit}
        onChange={(event) => {
          const seconds = Number(event.target.value);
          if (Number.isInteger(seconds) && seconds > 0 && seconds <= 3600) {
            onChange(seconds);
          }
        }}
        className="mt-2 rounded-xl"
        min="1"
        max="3600"
        step="1"
      />
      <p className="mt-1 text-xs text-muted-foreground">
        Defaults to 60 seconds. Students are automatically submitted when time expires.
      </p>
    </div>
  );
}

// Slide Settings Component (dynamic based on type)
function SlideSettings({
  slide,
  content,
  onUpdate
}: {
  slide: Slide;
  content: any;
  onUpdate: (field: string, value: any) => void;
}) {
  if (!slide) return null;

  switch (slide.type) {
    case 'text_image':
      return (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Columns className="w-4 h-4 text-muted-foreground" />
              <Label>Two-Column Layout</Label>
            </div>
            <Switch
              checked={content.layout === 'two-column'}
              onCheckedChange={(checked) => onUpdate('layout', checked ? 'two-column' : 'single')}
            />
          </div>
          <div>
            <Label>{content.layout === 'two-column' ? 'Column 1 Content' : 'Content'}</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.content || ''}
                onChange={(value) => onUpdate('content', value)}
                placeholder="Enter your content here..."
                minHeight="150px"
              />
            </div>
          </div>
          <div>
            <UploadableFileInput
              currentFileUrl={content.imageUrl}
              onFileUpload={(url) => onUpdate('imageUrl', url)}
              onFileRemove={() => onUpdate('imageUrl', null)}
              label={content.layout === 'two-column' ? 'Column 1 Image' : 'Image'}
              acceptedTypes="images"
            />
          </div>
          {content.layout === 'two-column' && (
            <>
              <div className="border-t border-border pt-4 mt-2">
                <Label>Column 2 Content</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={content.content2 || ''}
                    onChange={(value) => onUpdate('content2', value)}
                    placeholder="Enter column 2 content here..."
                    minHeight="150px"
                  />
                </div>
              </div>
              <div>
                <UploadableFileInput
                  currentFileUrl={content.imageUrl2}
                  onFileUpload={(url) => onUpdate('imageUrl2', url)}
                  onFileRemove={() => onUpdate('imageUrl2', null)}
                  label="Column 2 Image"
                  acceptedTypes="images"
                />
              </div>
            </>
          )}
        </>
      );

    case 'multiple_choice':
      return (
        <>
          <div>
            <Label>Question</Label>
            <Textarea
              value={content.question || ''}
              onChange={(e) => onUpdate('question', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="Enter your question..."
            />
          </div>
          <div>
            <Label>Options (one per line)</Label>
            <Textarea
              value={(content.options || []).join('\n')}
              onChange={(e) => onUpdate('options', e.target.value.split('\n').filter(Boolean))}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Option A&#10;Option B&#10;Option C&#10;Option D"
            />
          </div>
          <div>
            <Label>Correct Answer</Label>
            <Select
              value={String(content.correctAnswer ?? 0)}
              onValueChange={(v) => onUpdate('correctAnswer', parseInt(v))}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue placeholder="Select correct answer" />
              </SelectTrigger>
              <SelectContent>
                {(content.options || []).map((opt: string, i: number) => (
                  <SelectItem key={i} value={String(i)}>
                    {opt || `Option ${i + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div>
              <Label htmlFor="multiple-choice-shuffle-options">Shuffle answer options</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Students will see the choices in a shuffled order.
              </p>
            </div>
            <Switch
              id="multiple-choice-shuffle-options"
              checked={Boolean(content.shuffleOptions)}
              onCheckedChange={(checked) => onUpdate('shuffleOptions', checked)}
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'true_false':
      return (
        <>
          <div>
            <Label>Statement</Label>
            <Textarea
              value={content.question || ''}
              onChange={(e) => onUpdate('question', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="Enter a true or false statement..."
            />
          </div>
          <div>
            <Label>Correct Answer</Label>
            <Select
              value={content.correctAnswer ? 'true' : 'false'}
              onValueChange={(v) => onUpdate('correctAnswer', v === 'true')}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'open_ended':
      return (
        <>
          <div>
            <Label>Question</Label>
            <Textarea
              value={content.question || ''}
              onChange={(e) => onUpdate('question', e.target.value)}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Enter your question..."
            />
          </div>
          <div>
            <Label>Placeholder Text</Label>
            <Input
              value={content.placeholder || ''}
              onChange={(e) => onUpdate('placeholder', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="Type your answer here..."
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'poll':
      return (
        <>
          <div>
            <Label>Poll Question</Label>
            <Textarea
              value={content.question || ''}
              onChange={(e) => onUpdate('question', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="What do you think about...?"
            />
          </div>
          <div>
            <Label>Options (one per line)</Label>
            <Textarea
              value={(content.options || []).join('\n')}
              onChange={(e) => onUpdate('options', e.target.value.split('\n').filter(Boolean))}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Option 1&#10;Option 2&#10;Option 3"
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'video':
      return (
        <>
          <div>
            <Label>Video URL</Label>
            <Input
              value={content.videoUrl || ''}
              onChange={(e) => onUpdate('videoUrl', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
          <div>
            <Label>Provider</Label>
            <Select
              value={content.provider || 'youtube'}
              onValueChange={(v) => onUpdate('provider', v)}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="vimeo">Vimeo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'word_cloud':
      return (
        <>
          <div>
            <Label>Question/Prompt</Label>
            <Textarea
              value={content.question || ''}
              onChange={(e) => onUpdate('question', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="What word comes to mind when...?"
            />
          </div>
          <div>
            <Label>Max Words Per Student</Label>
            <Input
              type="number"
              value={content.maxWords || 3}
              onChange={(e) => onUpdate('maxWords', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
              max="10"
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'web_content':
      return (
        <>
          <div>
            <Label>Website URL</Label>
            <Input
              value={content.url || ''}
              onChange={(e) => onUpdate('url', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="https://example.com"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Embed a website directly into your lesson. Note: some sites (e.g. ChatGPT, Google, social media) block embedding — students will see an "Open in new tab" fallback instead.
            </p>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.description || ''}
                onChange={(value) => onUpdate('description', value)}
                placeholder="What should students explore on this website?"
                minHeight="100px"
              />
            </div>
          </div>
        </>
      );

    case 'field_trip':
      return (
        <>
          <div>
            <Label className="mb-2 block">Panorama Source</Label>
            <RadioGroup
              value={content.panoramaMode || 'url'}
              onValueChange={(value: string) => onUpdate('panoramaMode', value)}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="url" id="ft-url" />
                <Label htmlFor="ft-url" className="cursor-pointer font-normal">
                  External URL
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="upload" id="ft-upload" />
                <Label htmlFor="ft-upload" className="cursor-pointer font-normal">
                  Upload 360° Image
                </Label>
              </div>
            </RadioGroup>
          </div>

          {(content.panoramaMode || 'url') === 'url' ? (
            <div>
              <Label>360° Virtual Tour URL</Label>
              <Input
                value={content.panoramaUrl || ''}
                onChange={(e) => onUpdate('panoramaUrl', e.target.value)}
                className="mt-2 rounded-xl"
                placeholder="https://..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste an embeddable virtual tour link (Google Street View, Matterport, etc.)
              </p>
            </div>
          ) : (
            <div>
              <UploadableFileInput
                currentFileUrl={content.panoramaImageUrl}
                onFileUpload={(url) => onUpdate('panoramaImageUrl', url)}
                onFileRemove={() => onUpdate('panoramaImageUrl', null)}
                label="360° Image"
                acceptedTypes="images"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Upload an equirectangular 360° panorama image (JPG or PNG)
              </p>
            </div>
          )}

          <div>
            <Label>Location Name</Label>
            <Input
              value={content.location || ''}
              onChange={(e) => onUpdate('location', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="e.g., The Great Pyramids, Amazon Rainforest"
            />
          </div>
          <div>
            <Label>Description</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.description || ''}
                onChange={(value) => onUpdate('description', value)}
                placeholder="Describe this virtual field trip location..."
                minHeight="100px"
              />
            </div>
          </div>
        </>
      );

    case 'audio':
      return (
        <>
          <div>
            <Label>Audio URL</Label>
            <Input
              value={content.audioUrl || ''}
              onChange={(e) => onUpdate('audioUrl', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              MP3, WAV, or other audio file URL
            </p>
          </div>
          <div>
            <Label>Audio Title</Label>
            <Input
              value={content.audioTitle || ''}
              onChange={(e) => onUpdate('audioTitle', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="e.g., Historical Speech, Music Sample"
            />
          </div>
          <div>
            <Label>Transcript/Notes (optional)</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.transcript || ''}
                onChange={(value) => onUpdate('transcript', value)}
                placeholder="Provide a transcript or notes about the audio..."
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <Label>Show Controls</Label>
            <Select
              value={content.showControls ? 'true' : 'false'}
              onValueChange={(v) => onUpdate('showControls', v === 'true')}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes - Students can control playback</SelectItem>
                <SelectItem value="false">No - Auto-play only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'pdf':
      return (
        <>
          <div>
            <Label>PDF URL</Label>
            <Input
              value={content.pdfUrl || ''}
              onChange={(e) => onUpdate('pdfUrl', e.target.value)}
              className="mt-2 rounded-xl"
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Direct link to a PDF file
            </p>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.description || ''}
                onChange={(value) => onUpdate('description', value)}
                placeholder="What should students focus on in this PDF?"
                minHeight="100px"
              />
            </div>
          </div>
        </>
      );

    case 'text_only':
      return (
        <>
          <div>
            <Label>Background Color</Label>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="color"
                value={content.backgroundColor || '#ffffff'}
                onChange={(e) => onUpdate('backgroundColor', e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer p-0.5"
              />
              <Input
                value={content.backgroundColor || '#ffffff'}
                onChange={(e) => onUpdate('backgroundColor', e.target.value)}
                className="rounded-xl flex-1"
                placeholder="#ffffff"
                maxLength={7}
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {['#ffffff', '#f8fafc', '#fef3c7', '#d1fae5', '#dbeafe', '#ede9fe', '#fce7f3', '#1e293b', '#0f172a'].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => onUpdate('backgroundColor', color)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    content.backgroundColor === color ? 'border-primary ring-2 ring-primary/30 scale-110' : 'border-border hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
          <div>
            <Label>Content</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.content || ''}
                onChange={(value) => onUpdate('content', value)}
                placeholder="Enter your text content here..."
                minHeight="200px"
              />
            </div>
          </div>
        </>
      );

    case 'fill_blanks':
      return (
        <>
          <div>
            <Label>Sentence with Blanks</Label>
            <Textarea
              value={content.sentence || ''}
              onChange={(e) => onUpdate('sentence', e.target.value)}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Use [blank1], [blank2], etc. for answers&#10;Example: The capital of France is [blank1]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use [blank1], [blank2], [blank3] to mark answer spots
            </p>
          </div>
          <div>
            <Label>Answers (one per line, in order)</Label>
            <Textarea
              value={(content.answers || []).join('\n')}
              onChange={(e) => onUpdate('answers', e.target.value.split('\n').filter(Boolean))}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Paris&#10;Eiffel Tower&#10;France"
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'matching_pairs':
      {
        const pairs: { left: string; right: string }[] = content.pairs || [];
        const updatePair = (index: number, field: 'left' | 'right', value: string) => {
          onUpdate('pairs', pairs.map((pair, pairIndex) => (
            pairIndex === index ? { ...pair, [field]: value } : pair
          )));
        };

      return (
        <>
          <div>
            <Label>Instructions</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.instruction || ''}
                onChange={(value) => onUpdate('instruction', value)}
                placeholder="e.g., Match each term to its definition"
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Matching pairs</Label>
                <p className="mt-1 text-xs text-muted-foreground">Students will see the right-hand answers shuffled.</p>
              </div>
              <Badge variant="secondary">{pairs.length} {pairs.length === 1 ? 'pair' : 'pairs'}</Badge>
            </div>
            <div className="mt-3 space-y-3">
              {pairs.map((pair, index) => (
                <div key={index} className="rounded-xl border border-border bg-muted/20 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">PAIR {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={`Remove pair ${index + 1}`}
                      onClick={() => onUpdate('pairs', pairs.filter((_, pairIndex) => pairIndex !== index))}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                    <Input
                      value={pair.left || ''}
                      onChange={(event) => updatePair(index, 'left', event.target.value)}
                      className="rounded-lg bg-background"
                      aria-label={`Pair ${index + 1} left item`}
                      placeholder="Term or prompt"
                    />
                    <span className="hidden text-sm text-muted-foreground sm:block" aria-hidden="true">matches</span>
                    <Input
                      value={pair.right || ''}
                      onChange={(event) => updatePair(index, 'right', event.target.value)}
                      className="rounded-lg bg-background"
                      aria-label={`Pair ${index + 1} right match`}
                      placeholder="Correct match"
                    />
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => onUpdate('pairs', [...pairs, { left: '', right: '' }])}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add a pair
              </Button>
            </div>
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );
      }

    case 'drag_drop':
      {
        const targets: { id: string; label: string }[] = content.targets || [];
        const items: { id: string; label: string; targetId: string }[] = content.items || [];

      return (
        <>
          <div>
            <Label>Instruction</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.instruction || ''}
                onChange={(value) => onUpdate('instruction', value)}
                placeholder="e.g., Sort the animals into their correct habitats"
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Drop zones</Label>
                <p className="mt-1 text-xs text-muted-foreground">Create the categories students will sort items into.</p>
              </div>
              <Badge variant="secondary">{targets.length} {targets.length === 1 ? 'zone' : 'zones'}</Badge>
            </div>
            <div className="mt-3 space-y-2">
              {targets.map((target, index) => (
                <div key={target.id} className="flex items-center gap-2 rounded-xl border border-border bg-muted/20 p-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background text-xs font-semibold text-muted-foreground">
                    {index + 1}
                  </span>
                  <Input
                    value={target.label || ''}
                    onChange={(event) => onUpdate('targets', targets.map((entry, targetIndex) => (
                      targetIndex === index ? { ...entry, label: event.target.value } : entry
                    )))}
                    className="rounded-lg border-0 bg-transparent shadow-none focus-visible:ring-1"
                    aria-label={`Drop zone ${index + 1}`}
                    placeholder="Category name"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    aria-label={`Remove drop zone ${index + 1}`}
                    onClick={() => {
                      const remainingTargets = targets.filter((_, targetIndex) => targetIndex !== index);
                      onUpdate('targets', remainingTargets);
                      onUpdate('items', items.map((item) => (
                        item.targetId === target.id ? { ...item, targetId: '' } : item
                      )));
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => onUpdate('targets', [...targets, { id: crypto.randomUUID(), label: '' }])}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add a drop zone
              </Button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Draggable items</Label>
                <p className="mt-1 text-xs text-muted-foreground">Choose the correct drop zone for each item.</p>
              </div>
              <Badge variant="secondary">{items.length} {items.length === 1 ? 'item' : 'items'}</Badge>
            </div>
            <div className="mt-3 space-y-2">
              {items.map((item, index) => (
                <div key={item.id} className="grid gap-2 rounded-xl border border-border bg-muted/20 p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
                  <Input
                    value={item.label || ''}
                    onChange={(event) => onUpdate('items', items.map((entry, itemIndex) => (
                      itemIndex === index ? { ...entry, label: event.target.value } : entry
                    )))}
                    className="rounded-lg bg-background"
                    aria-label={`Draggable item ${index + 1}`}
                    placeholder={`Item ${index + 1}`}
                  />
                  <Select
                    value={item.targetId || 'unassigned'}
                    onValueChange={(targetId) => onUpdate('items', items.map((entry, itemIndex) => (
                      itemIndex === index ? { ...entry, targetId: targetId === 'unassigned' ? '' : targetId } : entry
                    )))}
                  >
                    <SelectTrigger className="rounded-lg bg-background">
                      <SelectValue placeholder="Choose a drop zone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Choose a drop zone</SelectItem>
                      {targets.map((target) => (
                        <SelectItem key={target.id} value={target.id}>{target.label || 'Unnamed drop zone'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 justify-self-end"
                    aria-label={`Remove item ${index + 1}`}
                    onClick={() => onUpdate('items', items.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => onUpdate('items', [...items, { id: crypto.randomUUID(), label: '', targetId: '' }])}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add an item
              </Button>
            </div>
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );
      }

    case 'sequencing':
      {
        const items: { id: string; label: string; description?: string }[] = content.items || [];
        const updateSequenceItem = (index: number, field: 'label' | 'description', value: string) => {
          const updatedItems = items.map((item, itemIndex) => (
            itemIndex === index ? { ...item, [field]: value } : item
          ));
          onUpdate('items', updatedItems);
          onUpdate('correctOrder', updatedItems.map((item) => item.id));
        };

      return (
        <>
          <div>
            <Label>Ordering Mode</Label>
            <Select
              value={content.sequenceMode || 'custom'}
              onValueChange={(val) => {
                onUpdate('sequenceMode', val);
                // Auto-update instruction based on mode
                const modeInstructions: Record<string, string> = {
                  custom: 'Drag the items into the correct order',
                  chronological: 'Arrange the items in chronological order (earliest → latest)',
                  steps: 'Arrange the steps in the correct order',
                  'smallest-to-largest': 'Arrange the items from smallest to largest',
                };
                if (!content.instruction || Object.values(modeInstructions).includes(content.instruction)) {
                  onUpdate('instruction', modeInstructions[val] || modeInstructions.custom);
                }
              }}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom Order</SelectItem>
                <SelectItem value="chronological">Chronological Order</SelectItem>
                <SelectItem value="steps">Steps in Order</SelectItem>
                <SelectItem value="smallest-to-largest">Smallest to Largest</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Choose how items should be ordered. This changes the labels shown to students.
            </p>
          </div>
          <div>
            <Label>Instruction</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.instruction || ''}
                onChange={(value) => onUpdate('instruction', value)}
                placeholder="e.g., Drag the events into the correct chronological order"
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Correct sequence</Label>
                <p className="mt-1 text-xs text-muted-foreground">Enter items in the correct order from top to bottom. Students see them shuffled.</p>
              </div>
              <Badge variant="secondary">{items.length} {items.length === 1 ? 'step' : 'steps'}</Badge>
            </div>
            <div className="mt-3 space-y-2">
              {items.map((item, index) => (
                <div key={item.id} className="flex gap-2 rounded-xl border border-border bg-muted/20 p-3">
                  <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background text-xs font-semibold text-muted-foreground">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input
                      value={item.label || ''}
                      onChange={(event) => updateSequenceItem(index, 'label', event.target.value)}
                      className="rounded-lg bg-background"
                      aria-label={`Sequence item ${index + 1} label`}
                      placeholder={`Step ${index + 1}`}
                    />
                    <Input
                      value={item.description || ''}
                      onChange={(event) => updateSequenceItem(index, 'description', event.target.value)}
                      className="rounded-lg bg-background"
                      aria-label={`Sequence item ${index + 1} description`}
                      placeholder="Description (optional)"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    aria-label={`Remove sequence item ${index + 1}`}
                    onClick={() => {
                      const updatedItems = items.filter((_, itemIndex) => itemIndex !== index);
                      onUpdate('items', updatedItems);
                      onUpdate('correctOrder', updatedItems.map((entry) => entry.id));
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => {
                  const updatedItems = [...items, { id: crypto.randomUUID(), label: '', description: '' }];
                  onUpdate('items', updatedItems);
                  onUpdate('correctOrder', updatedItems.map((item) => item.id));
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add a step
              </Button>
            </div>
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );
      }

case 'time_to_climb': {
      const quizType = content.quizType || 'multiple_choice';

      // Normalize: convert legacy single-question format to questions array
      const questions: { question: string; options: string[]; correctAnswer: number | string }[] = (() => {
        if (content.questions && Array.isArray(content.questions) && content.questions.length > 0) {
          return content.questions;
        }
        if (content.question && content.options) {
          return [{ question: content.question, options: content.options, correctAnswer: content.correctAnswer ?? 0 }];
        }
        if (quizType === 'identification') {
          return [{ question: '', options: [], correctAnswer: '' }];
        }
        return [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }];
      })();

      const updateQuestion = (index: number, field: string, value: any) => {
        const updated = [...questions];
        updated[index] = { ...updated[index], [field]: value };
        onUpdate('questions', updated);
      };

      const addQuestion = () => {
        if (quizType === 'identification') {
          onUpdate('questions', [...questions, { question: '', options: [], correctAnswer: '' }]);
        } else {
          onUpdate('questions', [...questions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
        }
      };

      const removeQuestion = (index: number) => {
        if (questions.length <= 1) return;
        onUpdate('questions', questions.filter((_, i) => i !== index));
      };

      const handleQuizTypeChange = (newType: string) => {
        onUpdate('quizType', newType);
        // Reset questions to match the new type
        if (newType === 'identification') {
          onUpdate('questions', [{ question: '', options: [], correctAnswer: '' }]);
        } else {
          onUpdate('questions', [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
        }
      };

      return (
        <>
          {/* Quiz Type Selector */}
          <div>
            <Label className="text-base font-semibold">Quiz Type</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={quizType === 'multiple_choice' ? 'default' : 'outline'}
                size="sm"
                className="rounded-xl flex-1"
                onClick={() => handleQuizTypeChange('multiple_choice')}
              >
                Multiple Choice
              </Button>
              <Button
                type="button"
                variant={quizType === 'identification' ? 'default' : 'outline'}
                size="sm"
                className="rounded-xl flex-1"
                onClick={() => handleQuizTypeChange('identification')}
              >
                Identification
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {quizType === 'identification'
                ? 'Students type their answer. Grading is case-insensitive.'
                : 'Students pick from a set of options.'}
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Questions ({questions.length})</Label>
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-1" />
                Add Question
              </Button>
            </div>

            {questions.map((q, qIndex) => (
              <div key={qIndex} className="border border-border rounded-xl p-4 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Question {qIndex + 1}</Label>
                  {questions.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(qIndex)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Question Text</Label>
                  <Textarea
                    value={q.question || ''}
                    onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                    className="mt-1 rounded-xl min-h-16"
                    placeholder="Enter a timed question"
                  />
                </div>

                {quizType === 'identification' ? (
                  <div>
                    <Label className="text-xs">Correct Answer</Label>
                    <Input
                      value={q.correctAnswer ?? ''}
                      onChange={(e) => updateQuestion(qIndex, 'correctAnswer', e.target.value)}
                      className="mt-1 rounded-xl"
                      placeholder="Type the correct answer"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Options (one per line)</Label>
                      <Textarea
                        value={(q.options || []).join('\n')}
                        onChange={(e) => updateQuestion(qIndex, 'options', e.target.value.split('\n').filter(Boolean))}
                        className="mt-1 rounded-xl min-h-20"
                        placeholder="Option A&#10;Option B&#10;Option C&#10;Option D"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Correct Answer</Label>
                      <Select
                        value={String(q.correctAnswer ?? 0)}
                        onValueChange={(v) => updateQuestion(qIndex, 'correctAnswer', parseInt(v))}
                      >
                        <SelectTrigger className="mt-1 rounded-xl">
                          <SelectValue placeholder="Select correct answer" />
                        </SelectTrigger>
                        <SelectContent>
                          {(q.options || []).map((opt: string, i: number) => (
                            <SelectItem key={i} value={String(i)}>
                              {opt || `Option ${i + 1}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div>
            <Label className="text-base font-semibold">Time Limit Applies To</Label>
            <RadioGroup
              value={content.timeLimitMode === 'all_questions' ? 'all_questions' : 'per_question'}
              onValueChange={(value) => onUpdate('timeLimitMode', value)}
              className="mt-2 grid grid-cols-2 gap-3"
            >
              <Label
                htmlFor="time-to-climb-time-limit-per-question"
                className="flex cursor-pointer items-center gap-2 rounded-xl border border-border p-3"
              >
                <RadioGroupItem id="time-to-climb-time-limit-per-question" value="per_question" />
                <span className="text-sm">Per question</span>
              </Label>
              <Label
                htmlFor="time-to-climb-time-limit-all-questions"
                className="flex cursor-pointer items-center gap-2 rounded-xl border border-border p-3"
              >
                <RadioGroupItem id="time-to-climb-time-limit-all-questions" value="all_questions" />
                <span className="text-sm">All questions</span>
              </Label>
            </RadioGroup>
          </div>
          <div>
            <Label>
              {content.timeLimitMode === 'all_questions'
                ? 'Total Time Limit for All Questions (seconds)'
                : 'Time Limit per Question (seconds)'}
            </Label>
            <Input
              type="number"
              value={content.timeLimit || 30}
              onChange={(e) => onUpdate('timeLimit', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="5"
              max="300"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div>
              <Label htmlFor="time-to-climb-shuffle-questions">Shuffle question order</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Students will get the same questions in a deterministic shuffled sequence.
              </p>
            </div>
            <Switch
              id="time-to-climb-shuffle-questions"
              checked={Boolean(content.shuffleQuestions)}
              onCheckedChange={(checked) => onUpdate('shuffleQuestions', checked)}
            />
          </div>
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );
    }

    case 'draw_it':
      return (
        <>
          <div>
            <Label>Instructions</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.instructions || ''}
                onChange={(value) => onUpdate('instructions', value)}
                placeholder="e.g., Draw a diagram of the water cycle"
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <UploadableFileInput
              currentFileUrl={content.backgroundImageUrl}
              onFileUpload={(url) => onUpdate('backgroundImageUrl', url)}
              onFileRemove={() => onUpdate('backgroundImageUrl', null)}
              label="Background Image"
              acceptedTypes="images"
            />
          </div>
          <div>
            <Label>Canvas Background Color (optional)</Label>
            <Input
              type="color"
              value={content.backgroundColor || '#ffffff'}
              onChange={(e) => onUpdate('backgroundColor', e.target.value)}
              className="mt-2 rounded-xl"
            />
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'collaborate_board':
      return (
        <>
          <div>
            <Label>Collaboration Prompt</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.prompt || ''}
                onChange={(value) => onUpdate('prompt', value)}
                placeholder="What should students collaborate on?"
                minHeight="100px"
              />
            </div>
          </div>
          <div>
            <Label>Background Template (optional)</Label>
            <Select
              value={content.template || 'blank'}
              onValueChange={(v) => onUpdate('template', v)}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">Blank Board</SelectItem>
                <SelectItem value="grid">Grid Layout</SelectItem>
                <SelectItem value="brainstorm">Brainstorm Map</SelectItem>
                <SelectItem value="timeline">Timeline</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Max Notes per Student</Label>
            <Input
              type="number"
              value={content.maxNotes ?? 3}
              onChange={(e) => onUpdate('maxNotes', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
              max="20"
            />
          </div>

          <div>
            <Label>Note Text Size</Label>
            <Select
              value={content.noteFontSize || 'small'}
              onValueChange={(v) => onUpdate('noteFontSize', v)}
            >
              <SelectTrigger className="mt-2 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'label_diagram':
      return (
        <>
          <div>
            <UploadableFileInput
              currentFileUrl={content.imageUrl}
              onFileUpload={(url) => onUpdate('imageUrl', url)}
              onFileRemove={() => onUpdate('imageUrl', null)}
              label="Diagram Image"
              acceptedTypes="images"
            />
          </div>
          <div>
            <Label>Labels to Add (one per line)</Label>
            <Textarea
              value={(content.labels || []).join('\n')}
              onChange={(e) => onUpdate('labels', e.target.value.split('\n').filter(Boolean))}
              className="mt-2 rounded-xl min-h-24"
              placeholder="Label 1&#10;Label 2&#10;Label 3"
            />
          </div>
          <div>
            <Label>Instructions</Label>
            <div className="mt-2">
              <RichTextEditor
                value={content.instructions || ''}
                onChange={(value) => onUpdate('instructions', value)}
                placeholder="e.g., Label the parts of the human heart"
                minHeight="100px"
              />
            </div>
          </div>
          <ActivityTimeLimitSetting
            value={content.timeLimit}
            onChange={(seconds) => onUpdate('timeLimit', seconds)}
          />
          <div>
            <Label>Points</Label>
            <Input
              type="number"
              value={content.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );

    case 'quiz': {
      const quizQuestions: { question: string; options: string[]; correctAnswer: number }[] = 
        Array.isArray(content.questions) && content.questions.length > 0
          ? content.questions
          : [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }];

      const updateQuizQuestion = (index: number, field: string, value: any) => {
        const updated = [...quizQuestions];
        updated[index] = { ...updated[index], [field]: value };
        onUpdate('questions', updated);
      };

      const addQuizQuestion = () => {
        onUpdate('questions', [...quizQuestions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
      };

      const removeQuizQuestion = (index: number) => {
        if (quizQuestions.length <= 1) return;
        onUpdate('questions', quizQuestions.filter((_, i) => i !== index));
      };

      return (
        <>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Quiz Questions ({quizQuestions.length})</Label>
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={addQuizQuestion}>
                <Plus className="w-4 h-4 mr-1" />
                Add Question
              </Button>
            </div>

            {quizQuestions.map((q, qIndex) => (
              <div key={qIndex} className="border border-border rounded-xl p-4 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Question {qIndex + 1}</Label>
                  {quizQuestions.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeQuizQuestion(qIndex)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Question Text</Label>
                  <Textarea
                    value={q.question || ''}
                    onChange={(e) => updateQuizQuestion(qIndex, 'question', e.target.value)}
                    className="mt-1 rounded-xl min-h-16"
                    placeholder="Enter quiz question"
                  />
                </div>
                <div>
                  <Label className="text-xs">Answer Options (one per line)</Label>
                  <Textarea
                    value={(q.options || []).join('\n')}
                    onChange={(e) => updateQuizQuestion(qIndex, 'options', e.target.value.split('\n').filter(Boolean))}
                    className="mt-1 rounded-xl min-h-20"
                    placeholder="Option A&#10;Option B&#10;Option C&#10;Option D"
                  />
                </div>
                <div>
                  <Label className="text-xs">Correct Answer</Label>
                  <Select
                    value={String(q.correctAnswer ?? 0)}
                    onValueChange={(v) => updateQuizQuestion(qIndex, 'correctAnswer', parseInt(v))}
                  >
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue placeholder="Select correct answer" />
                    </SelectTrigger>
                    <SelectContent>
                      {(q.options || []).map((opt: string, i: number) => (
                        <SelectItem key={i} value={String(i)}>
                          {opt || `Option ${i + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label>Time Limit (seconds)</Label>
            <Input
              type="number"
              value={content.timeLimit || 60}
              onChange={(e) => onUpdate('timeLimit', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="0"
            />
            <p className="text-xs text-muted-foreground mt-1">
              The timer resets for each question and defaults to 30 seconds.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div>
              <Label htmlFor="quiz-shuffle-questions">Shuffle question order</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Students will answer the quiz in a deterministic shuffled sequence.
              </p>
            </div>
            <Switch
              id="quiz-shuffle-questions"
              checked={Boolean(content.shuffleQuestions)}
              onCheckedChange={(checked) => onUpdate('shuffleQuestions', checked)}
            />
          </div>
          <div>
            <Label>Total Points</Label>
            <Input
              type="number"
              value={content.points || 100}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="mt-2 rounded-xl"
              min="1"
            />
          </div>
        </>
      );
    }

    default:
      return (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">
            Settings for {slide.type} coming soon
          </p>
        </div>
      );
  }
}